/**
 * Firebase Client SDK Configuration
 *
 * CRITICAL CONSTRAINTS:
 * - API keys are PUBLIC and safe to expose (they identify the Firebase project)
 * - Security is enforced by Firestore/Storage rules, NOT by hiding keys
 * - Environment variables MUST start with VITE_ to be accessible in Vite
 * - NO localStorage or sessionStorage usage anywhere in the app
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getFunctions, Functions } from 'firebase/functions';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Validate configuration
const validateConfig = (): void => {
  const requiredKeys: (keyof typeof firebaseConfig)[] = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
  ];

  const missing = requiredKeys.filter((key) => !firebaseConfig[key]);

  if (missing.length > 0) {
    throw new Error(
      `Missing Firebase configuration: ${missing.join(', ')}. ` +
      'Please ensure all VITE_FIREBASE_* environment variables are set.'
    );
  }
};

// Validate before initialization
validateConfig();

// Initialize Firebase
const app: FirebaseApp = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);
export const functions: Functions = getFunctions(app, 'northamerica-northeast2');

// Admin functions in us-central1 (for Vertex AI compatibility)
export const adminFunctions: Functions = getFunctions(app, 'northamerica-northeast2');

// Export the app instance
export default app;
